import { useEffect } from 'react';
import { View, Pressable, Text, Platform, StatusBar } from 'react-native';
import { blockyStrikerViewStyles as styles } from './BlockyStrikerView.styles';
import { BlockyStrikerWebView } from './BlockyStrikerWebView';
import { BlockyStrikerOverlays } from './BlockyStrikerOverlays';
import { WarmupToast } from '../WarmupToast/WarmupToast';
import { useBlockyStrikerGame } from '../../../src/quiz/hooks/useBlockyStrikerGame';
import { BLOCKY_STRIKER_TEST_IDS } from '../../../src/quiz/constants/blockyStriker.constants';

interface BlockyStrikerViewProps {
  onMoreGames: () => void;
}

/**
 * Fullscreen host for the Blocky Striker 3D runtime. DELIBERATE DIFFERENCE from Striker Run: the game
 * supports portrait AND landscape — the runtime resizes its own viewport on `window.resize`, so the host
 * must NOT letterbox, scale, rotate, or lock orientation; the WebView/iframe simply fills the view.
 */
export function BlockyStrikerView({ onMoreGames }: BlockyStrikerViewProps) {
  const game = useBlockyStrikerGame();

  // Hide status bar on native while the game is open.
  useEffect(() => {
    if (Platform.OS !== 'web') StatusBar.setHidden(true, 'fade');
    return () => { if (Platform.OS !== 'web') StatusBar.setHidden(false, 'fade'); };
  }, []);

  // Restart lives in-canvas — back leaves immediately (no exit summary), closing the funnel first.
  const handleBack = () => {
    game.confirmExit();
    onMoreGames();
  };

  return (
    <View style={styles.fullscreenContainer} testID={BLOCKY_STRIKER_TEST_IDS.view}>
      {/* Back arrow — always top-left, on top of everything */}
      <Pressable
        testID={BLOCKY_STRIKER_TEST_IDS.back}
        style={({ pressed }) => [styles.backButton, pressed && styles.pressedDim]}
        onPress={handleBack}
      >
        <Text style={styles.backArrow}>←</Text>
      </Pressable>

      <View style={styles.canvasWrap}>
        <BlockyStrikerWebView
          ref={(r) => game.bindWebView(r)}
          html={game.html}
          onMessage={game.onMessage}
        />
      </View>

      <BlockyStrikerOverlays status={game.status} onRetry={game.retry} />
      <WarmupToast message={game.toast} />
    </View>
  );
}
